import { action, computed, observable, reaction, runInAction } from "mobx";
import { RootStore } from "./rootStore";
import { IBanner } from "../models/banner";
import agent from "../api/agent";
import { toast } from "react-toastify";
import { history } from "../..";
import { SyntheticEvent } from "react";

const LIMIT = 10;

export default class BannerStore {
  rootStore: RootStore;
  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    reaction(
      () => this.predicate.keys(),
      () => {
        this.page = 0;
        this.bannerRegistry.clear();
        this.loadBanners();
      }
    );
  }

  @observable bannerRegistry = new Map();
  @observable banner: IBanner | null = null;
  @observable loadingInitial = false;
  @observable submitting = false;
  @observable uploadingImage = false;
  @observable target = "";
  @observable loading = false;
  @observable bannerCount = 0;
  @observable page = 1;
  @observable predicate = new Map();

  @action setPredicate = () => {
    this.predicate.clear();
  };

  @computed get axiosParams() {
    const params = new URLSearchParams();
    params.append("limit", String(LIMIT));
    params.append("offset", `${this.page > 1 ? (this.page - 1) * LIMIT : 0}`);
    this.predicate.forEach((value, key) => {
      params.append(key, value);
    });

    return params;
  }

  @computed get totalPages() {
    return Math.ceil(this.bannerCount / LIMIT);
  }

  @action setPage = (page: number) => {
    this.page = page;
  };

  @computed get bannersByCode() {
    const banners = Array.from(this.bannerRegistry.values());
    return banners;
  }

  @action loadBanners = async () => {
    this.loadingInitial = true;
    try {
      this.bannerRegistry.clear();
      const bannersEnvelope = await agent.Banners.list(this.axiosParams);
      const { dataCollection, dataCount } = bannersEnvelope;
      runInAction("loading banners", () => {
        dataCollection.forEach((banner) => {
          this.bannerRegistry.set(banner.id, banner);
        });
        this.bannerCount = dataCount;
        this.loadingInitial = false;
      });
    } catch (error) {
      runInAction("load banners error", () => {
        this.loadingInitial = false;
      });
      toast.error("Erro ao carregar banners");
    }
  };

  @action loadBanner = async (id: string) => {
    let banner = this.getBanner(id);
    if (banner) {
      this.banner = banner;
      return banner;
    } else {
      this.loadingInitial = true;
      try {
        banner = await agent.Banners.details(id);
        runInAction("loading banner", () => {
          this.banner = banner;
          this.bannerRegistry.set(banner.id, banner);
          this.loadingInitial = false;
        });
        return banner;
      } catch (error) {
        runInAction("load banner error", () => {
          this.loadingInitial = false;
        });
        toast.error("Erro ao carregar banner");
      }
    }
  };

  @action loadBannerByCode = async (code: string) => {
    this.loadingInitial = true;
    try {
      let banner = await agent.Banners.detailsByCode(code);
      runInAction("loading banner", () => {
        this.banner = banner;
        this.bannerRegistry.set(banner.id, banner);
        this.loadingInitial = false;
      });
      return banner;
    } catch (error) {
      runInAction("load banner error", () => {
        this.loadingInitial = false;
      });
      toast.error("Erro ao carregar banner");
    }
  };

  @action clearBanner = () => {
    this.banner = null;
  };

  getBanner = (id: string) => {
    return this.bannerRegistry.get(id);
  };

  @action createBanner = async (image: Blob) => {
    this.submitting = true;
    this.uploadingImage = true;
    try {
      let banner = await agent.Banners.create(image);
      runInAction("creating banner", () => {
        this.submitting = false;
        this.uploadingImage = false;
      });
      history.push(`/admin/banners/${banner.id}`);
    } catch (error) {
      runInAction("create banner error", () => {
        this.submitting = false;
        this.uploadingImage = false;
      });
      toast.error("Erro ao criar banner");
    }
  };

  @action editBanner = async (banner: IBanner) => {
    this.submitting = true;
    try {
      await agent.Banners.update(banner.id, banner.code, banner.redirectUrl);
      runInAction("editing banner", () => {
        this.bannerRegistry.set(banner.id, banner);
        this.banner = banner;
        this.submitting = false;
      });
      history.push("/admin/banners/");
    } catch (error) {
      runInAction("edit banner error", () => {
        this.submitting = false;
      });
    }
  };

  @action deleteBanner = async (
    event: SyntheticEvent<HTMLButtonElement>,
    id: string
  ) => {
    this.submitting = true;
    this.target = event.currentTarget.name;
    try {
      await agent.Banners.delete(id);
      runInAction("deleting banner", () => {
        this.bannerRegistry.delete(id);
        this.submitting = false;
        this.target = "";
      });
      history.push("/admin/banners");
    } catch (error) {
      runInAction("delete banner error", () => {
        this.submitting = false;
        this.target = "";
      });
    }
  };
}
